import { useMemo, useState } from "react";
import { Layers, Plus } from "lucide-react";
import CreateCollectionDialog from "./CreateCollectionDialog";
import MintSupplyDialog from "./MintSupplyDialog";
import {
  CATEGORY_LABEL, CopyHex, EmptyState, ErrorBanner, FilterChips, RowsSkeleton, SearchInput,
  SectionHeader, StatusBadge, collectionStatus, fmtNum, fmtUsd,
} from "./bcShared";

const COLS = "md:grid-cols-[2fr,0.8fr,0.8fr,1.6fr,0.9fr,auto]";

export default function BlockchainCollections({ collections, loading, error, paused, onRetry, onChanged }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [minting, setMinting] = useState(null);

  const isIssue = (c) => collectionStatus(c).tone !== "success";
  const issues = collections.filter(isIssue).length;

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return collections.filter((c) => {
      if (filter === "live" && isIssue(c)) return false;
      if (filter === "issues" && !isIssue(c)) return false;
      return !needle || [c.name, c.symbol, c.category].some((v) => v?.toLowerCase().includes(needle));
    });
  }, [collections, filter, q]);

  return (
    <div className="zc-card overflow-hidden zc-stack-table" data-testid="bc-collections">
      <SectionHeader title="Token collections" count={collections.length}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <FilterChips
              value={filter}
              onChange={setFilter}
              options={[
                { id: "all", label: "All", count: collections.length },
                { id: "live", label: "Live", count: collections.length - issues },
                { id: "issues", label: "Issues", count: issues },
              ]}
            />
            <SearchInput value={q} onChange={setQ} placeholder="Search name, symbol, category…" />
          </div>
          <button type="button" onClick={() => setCreating(true)} className="zc-btn-secondary px-4 h-9 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="bc-new-collection">
            <Plus size={14} /> New collection
          </button>
        </div>
      </SectionHeader>

      {error && <ErrorBanner message={error} onRetry={onRetry} />}

      <div className={`hidden md:grid ${COLS} gap-4 zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold`}>
        <div>Collection</div><div>Price</div><div>Token</div><div>Supply</div><div>Status</div><div className="w-24" />
      </div>

      {loading && collections.length === 0 && <RowsSkeleton />}
      {!loading && !error && rows.length === 0 && (
        collections.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No collections yet"
            hint="Create the first tokenized car collection to start selling fractions."
            action={<button type="button" onClick={() => setCreating(true)} className="zc-btn-secondary px-4 h-9 text-xs font-semibold">New collection</button>}
          />
        ) : (
          <EmptyState icon={Layers} title="Nothing matches" hint="Try a different filter or search." />
        )
      )}

      {rows.map((c) => {
        const st = collectionStatus(c);
        const sold = (c.totalSupply || 0) - (c.availableForSale || 0);
        const pct = c.totalSupply ? Math.min(100, (sold / c.totalSupply) * 100) : 0;
        const canMint = c.status === "MINTED" && c.onChain !== false;
        return (
          <div key={c.id} className={`zc-stack-row md:grid ${COLS} md:items-center gap-4 px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0 text-sm`} data-testid={`bc-collection-${c.symbol}`}>
            <div data-label="Collection" className="min-w-0 flex items-center gap-3">
              <img src={c.imageUrl} alt="" className="w-11 h-11 rounded-lg object-cover bg-[var(--zc-bg-3)] shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
              <div className="min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-2xs text-[var(--zc-text-3)] mt-0.5">
                  <span className="font-mono">{c.symbol}</span> · {CATEGORY_LABEL[c.category] || c.category}
                </div>
              </div>
            </div>
            <div data-label="Price" className="font-semibold tabular-nums">{fmtUsd(c.unitPriceCents)}</div>
            <div data-label="Token" className="min-w-0">
              <div className="tabular-nums">{c.tokenId != null ? `#${c.tokenId}` : "—"}</div>
              {c.mintTxHashes?.length > 0 && (
                <CopyHex value={c.mintTxHashes[c.mintTxHashes.length - 1]} label="Mint transaction" className="text-2xs mt-0.5" />
              )}
            </div>
            <div data-label="Supply" className="min-w-0">
              <div className="flex justify-between text-xs tabular-nums mb-1.5">
                <span><b>{fmtNum(sold)}</b> sold</span>
                <span className="text-[var(--zc-text-3)]">{fmtNum(c.availableForSale)} left of {fmtNum(c.totalSupply)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[var(--zc-bg-3)] overflow-hidden">
                <div className="h-full rounded-full bg-[var(--zc-secondary)]" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div data-label="Status"><StatusBadge tone={st.tone}>{st.label}</StatusBadge></div>
            <div data-label="Actions" className="zc-stack-actions md:w-24 md:flex md:justify-end">
              <button
                type="button"
                onClick={() => setMinting(c)}
                disabled={!canMint}
                title={canMint ? "Increase supply" : "Only live, on-chain collections can be minted"}
                className="zc-btn-ghost px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`bc-mint-${c.symbol}`}
              >
                <Plus size={12} /> Mint
              </button>
            </div>
          </div>
        );
      })}

      <CreateCollectionDialog
        open={creating}
        onOpenChange={setCreating}
        paused={paused}
        existingSymbols={collections.map((c) => c.symbol)}
        onCreated={onChanged}
      />
      <MintSupplyDialog collection={minting} paused={paused} onOpenChange={(v) => !v && setMinting(null)} onMinted={onChanged} />
    </div>
  );
}
